/**
 * useTurnCredentials.ts
 *
 * Fetches short-lived ICE/TURN credentials from the Camlin runtime-service
 * (`/api/turn-credentials`) and writes them into the module-level cache
 * read by `voiceCallFeature.makeCall` when building `pcConfig.iceServers`.
 *
 * Mounted exactly once per app instance from `useHostedLiveController` so
 * every avatar consumer (DM, AgentLink, builder live mode) gets fresh
 * Twilio NTS tokens without any consumer-side change.
 *
 * Lifecycle:
 *   1. On mount: fire one fetch. While in-flight, the module cache is
 *      empty so `makeCall` would fall back to STUN-only — a tradeoff
 *      worth taking because (a) the fetch is fast (~200ms p95), (b) the
 *      user has to physically click Listen first, and (c) the next
 *      makeCall after the fetch resolves picks up the fresh creds.
 *   2. On success: write to cache + schedule a refresh at 75% of TTL.
 *   3. On error: warn + schedule a 30s retry. Cache stays empty so the
 *      consumer falls through to STUN-only with the warning logged in
 *      `turnCredentialsClient`.
 *   4. On unmount: cancel the refresh timer; leave the cache populated
 *      (any subsequent `makeCall` from elsewhere should still get a
 *      working set of creds).
 *
 * Why fetch once + refresh on a timer instead of fetch-per-call?
 *   - `makeCall` is sync, so it can't `await` a fetch. The refresh
 *     pattern means the cache is always warm by the time Listen is
 *     pressed (which happens after the avatar mounts → after this
 *     hook has had hundreds of ms to do its work).
 *   - Twilio NTS is rate-limited; fetch-per-call would make us
 *     vulnerable to a Listen-spam DoS against our own Twilio quota.
 */

import { useEffect, useRef, useState } from 'react'

import {
  setCachedTurnCredentials,
  clearCachedTurnCredentials,
} from '../../channels/voice/turnCredentialsClient'

// 30s after a transient failure. Keep this short enough that the
// next user click almost always lands on a successful fetch, but long
// enough that an outage doesn't hammer Twilio.
const RETRY_AFTER_FAILURE_MS = 30_000

// Refresh at 75% of the server-supplied TTL. Server TTL defaults to
// 4h, so this is ~3h. Fetch happens in the background while the cache
// is still valid → zero user-visible disruption.
const REFRESH_AFTER_RATIO = 0.75

interface TurnCredentialsResponse {
  iceServers: RTCIceServer[]
  expiresAt: number
  ttlSeconds: number
  provider: 'twilio-nts' | 'stun-only'
  warning?: string
}

export interface UseTurnCredentialsOptions {
  /**
   * Base URL of the runtime-service (no trailing slash). Same value
   * the hosted live controller uses for visual-channel WebSockets.
   * If undefined / empty, the hook does nothing (consumer falls
   * through to STUN-only).
   */
  runtimeUrl?: string | null
  /**
   * Test-only override. When set, the hook calls this instead of
   * `window.fetch`. Lets unit tests inject a mock without touching
   * global fetch.
   */
  fetchOverride?: typeof fetch
  /**
   * Disable the hook entirely. Useful when the caller already knows
   * WebRTC live mode is off (visual-only, etc.) — no point spending
   * the round-trip.
   */
  enabled?: boolean
}

export interface UseTurnCredentialsState {
  status: 'idle' | 'loading' | 'ready' | 'error'
  provider: 'twilio-nts' | 'stun-only' | null
  expiresAt: number | null
  /**
   * Last error message (or warning surfaced by the server, e.g.
   * Twilio reachability issues that caused a STUN-only response).
   */
  lastError: string | null
}

const INITIAL_STATE: UseTurnCredentialsState = {
  status: 'idle',
  provider: null,
  expiresAt: null,
  lastError: null,
}

function buildEndpointUrl(runtimeUrl: string): string {
  const trimmed = runtimeUrl.replace(/\/+$/, '')
  return `${trimmed}/api/turn-credentials`
}

export function useTurnCredentials({
  runtimeUrl,
  fetchOverride,
  enabled = true,
}: UseTurnCredentialsOptions): UseTurnCredentialsState {
  const [state, setState] = useState<UseTurnCredentialsState>(INITIAL_STATE)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelledRef = useRef(false)
  // Treat each (runtimeUrl, enabled) pair as the identity of an
  // attempt. When the consumer changes runtime URL or disables the
  // hook, we want any in-flight refresh from the previous attempt to
  // become a no-op rather than racing the new state.
  const attemptIdRef = useRef(0)

  useEffect(() => {
    cancelledRef.current = false
    const attemptId = ++attemptIdRef.current

    const clearRefreshTimer = () => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
    }

    if (!enabled || !runtimeUrl || !runtimeUrl.trim()) {
      // Hook explicitly disabled or no endpoint configured. Don't
      // poison the cache with whatever was there from a previous
      // mount — the consumer (e.g. visual-only mode) has decided
      // they don't need TURN.
      setState(INITIAL_STATE)
      clearRefreshTimer()
      return () => {
        cancelledRef.current = true
        clearRefreshTimer()
      }
    }

    const endpoint = buildEndpointUrl(runtimeUrl)
    const fetcher = fetchOverride ?? (typeof fetch === 'function' ? fetch : undefined)
    if (!fetcher) {
      console.warn('[useTurnCredentials] global fetch unavailable; cannot mint ICE creds.')
      return () => {
        cancelledRef.current = true
        clearRefreshTimer()
      }
    }

    const fetchOnce = async () => {
      if (cancelledRef.current || attemptIdRef.current !== attemptId) {
        return
      }

      setState((prev) => ({ ...prev, status: 'loading' }))

      try {
        const response = await fetcher(endpoint, {
          method: 'GET',
          credentials: 'omit',
        })
        if (cancelledRef.current || attemptIdRef.current !== attemptId) {
          return
        }
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        const data = (await response.json()) as TurnCredentialsResponse

        if (cancelledRef.current || attemptIdRef.current !== attemptId) {
          return
        }

        setCachedTurnCredentials({
          iceServers: data.iceServers,
          expiresAt: data.expiresAt,
          provider: data.provider,
          warning: data.warning,
        })

        setState({
          status: 'ready',
          provider: data.provider,
          expiresAt: data.expiresAt,
          lastError: data.warning ?? null,
        })

        // Schedule the next refresh at 75% of TTL. Floor at 60s and
        // cap at 1h so a misconfigured server-side TTL can't either
        // hammer us or leave us stale forever.
        const ttlMs = Math.max(0, data.expiresAt - Date.now())
        const refreshInMs = Math.min(60 * 60_000, Math.max(60_000, Math.floor(ttlMs * REFRESH_AFTER_RATIO)))
        clearRefreshTimer()
        refreshTimerRef.current = setTimeout(() => {
          refreshTimerRef.current = null
          void fetchOnce()
        }, refreshInMs)
      } catch (error) {
        if (cancelledRef.current || attemptIdRef.current !== attemptId) {
          return
        }
        const message = error instanceof Error ? error.message : String(error)
        console.warn(
          `[useTurnCredentials] Failed to fetch ICE creds from ${endpoint}: ${message}. ` +
            `Falling back to STUN-only until next retry in ${RETRY_AFTER_FAILURE_MS}ms.`,
        )
        setState((prev) => ({
          status: 'error',
          provider: prev.provider,
          expiresAt: prev.expiresAt,
          lastError: message,
        }))
        // Don't clear the cache — if we previously had real TURN
        // creds and the refresh failed, the existing creds may
        // still be valid (Twilio honours the token until expiry,
        // not until refresh).
        clearRefreshTimer()
        refreshTimerRef.current = setTimeout(() => {
          refreshTimerRef.current = null
          void fetchOnce()
        }, RETRY_AFTER_FAILURE_MS)
      }
    }

    void fetchOnce()

    return () => {
      cancelledRef.current = true
      clearRefreshTimer()
    }
    // We deliberately exclude `fetchOverride` from the dep array — it's
    // a test seam and shouldn't trigger refetches in production. The
    // intentional triggers are runtimeUrl + enabled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeUrl, enabled])

  // Test-only escape hatch: clear the module cache when the hook
  // unmounts entirely. We do NOT want this in production because the
  // cache outlives a single React tree mount cycle (e.g. fast-refresh
  // remounts) and re-fetching on every unmount would defeat the cache
  // entirely. The check below is a no-op in production.
  useEffect(() => {
    return () => {
      // intentionally left blank — see comment above.
      // (clearCachedTurnCredentials is exported so tests can call it
      // directly after unmount if they need a clean slate.)
      void clearCachedTurnCredentials
    }
  }, [])

  return state
}
